// Utility functions

import { FeedbackMetadata } from './types';

/**
 * Collect browser and page metadata
 */
export function collectMetadata(customMetadata?: Record<string, any>): FeedbackMetadata {
  const viewport = `${window.innerWidth}x${window.innerHeight}`;
  const browser = detectBrowser();

  return {
    url: window.location.href,
    browser,
    viewport,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...customMetadata,
  };
}

/**
 * Detect browser name and version
 */
function detectBrowser(): string {
  const ua = navigator.userAgent;
  let browser = 'Unknown';

  if (ua.includes('Firefox/')) {
    const version = ua.match(/Firefox\/(\d+)/)?.[1];
    browser = `Firefox ${version}`;
  } else if (ua.includes('Chrome/') && !ua.includes('Edg')) {
    const version = ua.match(/Chrome\/(\d+)/)?.[1];
    browser = `Chrome ${version}`;
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const version = ua.match(/Version\/(\d+)/)?.[1];
    browser = `Safari ${version}`;
  } else if (ua.includes('Edg/')) {
    const version = ua.match(/Edg\/(\d+)/)?.[1];
    browser = `Edge ${version}`;
  }

  return browser;
}

/**
 * Capture screenshot of current page
 */
export async function captureScreenshot(): Promise<string | null> {
  try {
    // Use html2canvas library if available
    if (typeof (window as any).html2canvas !== 'undefined') {
      const canvas = await (window as any).html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      return canvas.toDataURL('image/png');
    }

    // Fallback to native screenshot API if supported (limited browser support)
    if ('mediaDevices' in navigator && 'getDisplayMedia' in navigator.mediaDevices) {
      // This requires user interaction and shows a permission dialog
      // Not ideal for automatic screenshots
      console.warn('Screenshot capture requires html2canvas library');
      return null;
    }

    return null;
  } catch (error) {
    console.error('Screenshot capture failed:', error);
    return null;
  }
}

/**
 * Simple rate limiter using localStorage
 */
export class RateLimiter {
  private key: string;
  private maxAttempts: number;
  private windowMs: number;

  constructor(key: string, maxAttempts: number = 3, windowMs: number = 5 * 60 * 1000) {
    this.key = `triagly_ratelimit_${key}`;
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  canProceed(): boolean {
    const now = Date.now();
    const data = this.getData();

    // Filter out old attempts
    const recentAttempts = data.attempts.filter(
      (timestamp) => now - timestamp < this.windowMs
    );

    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    return true;
  }

  recordAttempt(): void {
    const now = Date.now();
    const data = this.getData();

    // Add new attempt
    data.attempts.push(now);

    // Keep only recent attempts
    data.attempts = data.attempts.filter(
      (timestamp) => now - timestamp < this.windowMs
    );

    this.setData(data);
  }

  getTimeUntilReset(): number {
    const now = Date.now();
    const data = this.getData();

    if (data.attempts.length === 0) {
      return 0;
    }

    const oldestAttempt = Math.min(...data.attempts);
    const resetTime = oldestAttempt + this.windowMs;

    return Math.max(0, resetTime - now);
  }

  private getData(): { attempts: number[] } {
    try {
      const stored = localStorage.getItem(this.key);
      return stored ? JSON.parse(stored) : { attempts: [] };
    } catch {
      return { attempts: [] };
    }
  }

  private setData(data: { attempts: number[] }): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to store rate limit data:', error);
    }
  }
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Console log capture for debugging
 */
export interface ConsoleLog {
  level: 'error' | 'warn' | 'log';
  message: string;
  timestamp: string;
  stack?: string;
}

export class ConsoleLogger {
  private buffer: ConsoleLog[] = [];
  private maxLogs: number;
  private levels: Set<'error' | 'warn' | 'log'>;
  private originalConsole: {
    error: typeof console.error;
    warn: typeof console.warn;
    log: typeof console.log;
  };
  private isActive: boolean = false;

  constructor(maxLogs: number = 50, levels: ('error' | 'warn' | 'log')[] = ['error', 'warn']) {
    this.maxLogs = maxLogs;
    this.levels = new Set(levels);
    
    // Store original console methods
    this.originalConsole = {
      error: console.error,
      warn: console.warn,
      log: console.log,
    };
  }

  /**
   * Start capturing console logs
   */
  start(): void {
    if (this.isActive) return;
    this.isActive = true;

    // Intercept console.error
    if (this.levels.has('error')) {
      console.error = (...args: any[]) => {
        this.captureLog('error', args);
        this.originalConsole.error.apply(console, args);
      };
    }

    // Intercept console.warn
    if (this.levels.has('warn')) {
      console.warn = (...args: any[]) => {
        this.captureLog('warn', args);
        this.originalConsole.warn.apply(console, args);
      };
    }

    // Intercept console.log
    if (this.levels.has('log')) {
      console.log = (...args: any[]) => {
        this.captureLog('log', args);
        this.originalConsole.log.apply(console, args);
      };
    }
  }

  /**
   * Stop capturing and restore original console methods
   */
  stop(): void {
    if (!this.isActive) return;
    this.isActive = false;

    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    console.log = this.originalConsole.log;
  }

  /**
   * Capture a log entry
   */
  private captureLog(level: 'error' | 'warn' | 'log', args: any[]): void {
    try {
      // Convert arguments to string
      const message = args.map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }).join(' ');

      // Get stack trace for errors
      let stack: string | undefined;
      if (level === 'error') {
        const error = args.find(arg => arg instanceof Error);
        if (error) {
          stack = error.stack;
        } else {
          // Create stack trace
          stack = new Error().stack?.split('\n').slice(2).join('\n');
        }
      }

      // Sanitize sensitive data
      const sanitized = this.sanitize(message);
      const sanitizedStack = stack ? this.sanitize(stack) : undefined;

      // Add to buffer
      const logEntry: ConsoleLog = {
        level,
        message: sanitized,
        timestamp: new Date().toISOString(),
        stack: sanitizedStack,
      };

      this.buffer.push(logEntry);

      // Keep buffer size limited (circular buffer)
      if (this.buffer.length > this.maxLogs) {
        this.buffer.shift();
      }
    } catch (error) {
      // Don't let logging break the app
      this.originalConsole.error('Failed to capture log:', error);
    }
  }

  /**
   * Sanitize sensitive data from logs
   */
  private sanitize(text: string): string {
    return text
      // API keys, tokens, secrets
      .replace(/[a-zA-Z0-9_-]*token[a-zA-Z0-9_-]*\s*[:=]\s*["']?[\w-]{20,}["']?/gi, 'token=***')
      .replace(/[a-zA-Z0-9_-]*key[a-zA-Z0-9_-]*\s*[:=]\s*["']?[\w-]{20,}["']?/gi, 'key=***')
      .replace(/[a-zA-Z0-9_-]*secret[a-zA-Z0-9_-]*\s*[:=]\s*["']?[\w-]{20,}["']?/gi, 'secret=***')
      // GitHub tokens (ghp_, gho_, etc.)
      .replace(/gh[ps]_[a-zA-Z0-9]{36,}/g, 'gh*_***')
      // JWT tokens
      .replace(/eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g, 'jwt.***')
      // Passwords
      .replace(/password\s*[:=]\s*["']?[^"'\s]+["']?/gi, 'password=***')
      // Email addresses
      .replace(/\b[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,}\b/g, '***@***.com')
      // Credit cards (basic pattern)
      .replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, '****-****-****-****')
      // URLs with tokens in query params
      .replace(/([?&])(token|key|secret|auth)=[^&\s]+/gi, '$1$2=***');
  }

  /**
   * Get all captured logs
   */
  getLogs(): ConsoleLog[] {
    return [...this.buffer];
  }

  /**
   * Clear all captured logs
   */
  clear(): void {
    this.buffer = [];
  }

  /**
   * Get logs count
   */
  getCount(): number {
    return this.buffer.length;
  }
}
